// useLayoutEditorDragDrop.js

import { useCallback, useMemo, useState } from 'react';

// Utilities
import {
	canMoveNodeToParent,
	findParentNodeKey,
	isLayoutEditorTextInteractionTarget,
	moveNodeToTarget,
	swapNodeWithEmptySlot,
} from './pageEditorLayoutUtils';

/**
 * useLayoutEditorDragDrop
 *
 * Owns the layout editor drag state and drag/drop handlers for slot swaps and insert targets.
 *
 * @param {object} params - Hook parameters.
 * @param {function|null} [params.dismissMenu=null] - Optional callback to dismiss the slot menu before dragging.
 * @param {function|null} [params.handleClearToolbar=null] - Optional callback to clear toolbar focus before dragging.
 * @param {object} params.layout - Current layout tree.
 * @param {function} params.persistLayout - Persists layout-only changes.
 *
 * @returns {object} Drag state values and drag/drop handlers.
 */
const useLayoutEditorDragDrop = ({
	dismissMenu = null,
	handleClearToolbar = null,
	layout,
	persistLayout,
}) => {
	const [dragOverTarget, setDragOverTarget] = useState(null);
	const [draggingNodeKey, setDraggingNodeKey] = useState('');
	const draggingNode = useMemo(() => layout.nodes[draggingNodeKey] || null, [draggingNodeKey, layout.nodes]);

	const resetDragState = useCallback(() => {
		setDragOverTarget(null);
		setDraggingNodeKey('');
	}, []);

	const handleDragStart = useCallback((nodeKey, event) => {
		if (!nodeKey || !event?.dataTransfer) return;
		if (isLayoutEditorTextInteractionTarget(event.target)) {
			event.preventDefault();
			event.stopPropagation();
			return;
		}

		event.stopPropagation();
		event.dataTransfer.effectAllowed = 'move';
		event.dataTransfer.setData('text/plain', nodeKey);

		dismissMenu?.();
		handleClearToolbar?.();
		setDraggingNodeKey(nodeKey);
	}, [dismissMenu, handleClearToolbar]);

	const handleDragEnd = useCallback(() => {
		resetDragState();
	}, [resetDragState]);

	const handleEmptySlotDragOver = useCallback((parentKey, targetNodeKey, event) => {
		const parentNode = layout.nodes[parentKey];
		if (!draggingNode || !canMoveNodeToParent(draggingNode, parentNode)) return;

		event.preventDefault();
		event.stopPropagation();
		event.dataTransfer.dropEffect = 'move';

		const isSameTarget = dragOverTarget
			&& dragOverTarget.edge === 'empty-slot'
			&& dragOverTarget.parentKey === parentKey
			&& dragOverTarget.targetNodeKey === targetNodeKey;

		if (!isSameTarget) {
			setDragOverTarget({
				edge: 'empty-slot',
				insertIndex: null,
				parentKey,
				targetNodeKey,
			});
		}
	}, [dragOverTarget, draggingNode, layout.nodes]);

	const applyDragDropTarget = useCallback(async (target) => {
		if (!draggingNodeKey || !target) return;

		let nextLayout = null;

		if (target.edge === 'empty-slot' && String(target.targetNodeKey || '').trim()) {
			nextLayout = swapNodeWithEmptySlot(layout, draggingNodeKey, target.targetNodeKey);
		}

		if (target.edge === 'insert' && String(target.parentKey || '').trim() && Number.isInteger(target.insertIndex)) {
			nextLayout = moveNodeToTarget(layout, draggingNodeKey, target.parentKey, target.insertIndex);
		}

		resetDragState();

		if (!nextLayout) return;
		await persistLayout(nextLayout);
	}, [draggingNodeKey, layout, persistLayout, resetDragState]);

	const handleEmptySlotDrop = useCallback(async (targetNodeKey, event) => {
		if (!draggingNodeKey) return;

		event.preventDefault();
		event.stopPropagation();

		await applyDragDropTarget({
			edge: 'empty-slot',
			parentKey: findParentNodeKey(layout, targetNodeKey),
			targetNodeKey,
		});
	}, [applyDragDropTarget, draggingNodeKey, layout]);

	const handleInsertDragOver = useCallback((parentKey, insertIndex, insertTargetKey, event) => {
		const parentNode = layout.nodes[parentKey];
		if (!draggingNode || !canMoveNodeToParent(draggingNode, parentNode)) return;

		event.preventDefault();
		event.stopPropagation();
		event.dataTransfer.dropEffect = 'move';

		const isSameTarget = dragOverTarget
			&& dragOverTarget.edge === 'insert'
			&& dragOverTarget.insertIndex === insertIndex
			&& dragOverTarget.parentKey === parentKey
			&& dragOverTarget.targetNodeKey === insertTargetKey;

		if (!isSameTarget) {
			setDragOverTarget({
				edge: 'insert',
				insertIndex,
				parentKey,
				targetNodeKey: insertTargetKey,
			});
		}
	}, [dragOverTarget, draggingNode, layout.nodes]);

	const handleInsertDrop = useCallback(async (parentKey, insertIndex, event) => {
		if (!draggingNodeKey) return;

		event.preventDefault();
		event.stopPropagation();

		await applyDragDropTarget({
			edge: 'insert',
			insertIndex,
			parentKey,
			targetNodeKey: `${parentKey}:insert:${insertIndex}`,
		});
	}, [applyDragDropTarget, draggingNodeKey]);

	const handleStickyDropZoneDragOver = useCallback((event) => {
		if (!draggingNodeKey || !dragOverTarget) return;

		event.preventDefault();
		event.dataTransfer.dropEffect = 'move';
	}, [dragOverTarget, draggingNodeKey]);

	const handleStickyDropZoneDrop = useCallback(async (event) => {
		if (!draggingNodeKey || !dragOverTarget) return;

		event.preventDefault();
		event.stopPropagation();

		await applyDragDropTarget(dragOverTarget);
	}, [applyDragDropTarget, dragOverTarget, draggingNodeKey]);

	return {
		dragOverTarget,
		draggingNode,
		draggingNodeKey,
		handleDragEnd,
		handleDragStart,
		handleEmptySlotDragOver,
		handleEmptySlotDrop,
		handleInsertDragOver,
		handleInsertDrop,
		handleStickyDropZoneDragOver,
		handleStickyDropZoneDrop,
	};
};

export default useLayoutEditorDragDrop;